import React from 'react';
import {
  FilterSideBar,
  KalturaWidget,
  useFeature,
} from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import { Text, TextVariants, Switch } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { KALTURA_CONFIGS } from 'config/constants';
import { EnablementFeatureFlags } from 'config/featureFlags';
import RangeFilters from './RangeFilters';

import './styles.scss';

type Props = {
  title?: string;
  /**
   *  Callback when a catalog filter is selected. Does not apply to the range filters.
   */
  onFilterSelect?: (groupKey: string, option: string) => void;
  /**
   * Whether to show the catalog filters like categories, products, etc. Defaults to false.
   */
  showCatalogFilters?: boolean;
  /**
   * Whether to show the full seats filter. Defaults to false.
   */
  showFullSeatsFilter?: boolean;
};

const LiveSessionFilters = ({
  title,
  onFilterSelect,
  showCatalogFilters = false,
  showFullSeatsFilter = false,
}: Props) => {
  const { t } = useTranslation();
  const { premiumSessionFilterStore } = useMobxStores();
  const filterConfigs = premiumSessionFilterStore.getFilterConfigs();

  const showPremiumSessionsWithScheduleDisabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowPremvtScheduleDisabled,
    defaultFlagValue: false,
  });

  const filterGroups = React.useMemo(() => {
    if (!showCatalogFilters) {
      return [];
    }
    return filterConfigs.map((filterConfig) => ({
      title: filterConfig.title,
      groupKey: filterConfig.key,
      'data-analytics-id': `${filterConfig.key}-filt-liveSess-lp`,
      options:
        premiumSessionFilterStore.filters[filterConfig.key]?.map((option) => {
          const optionAnalyticsId = `${option.key}-${filterConfig.key}-filt-liveSess-lp`;

          return {
            ...option,
            title: option.value,
            navItemProps: {
              'data-analytics-id': `container-${optionAnalyticsId}`,
            },
            checkboxProps: {
              'data-analytics-id': optionAnalyticsId,
            },
          };
        }) || [],
      mergeIdenticalLabels: filterConfig.groupIdenticalLabels,
    }));
  }, [filterConfigs, premiumSessionFilterStore.filters, showCatalogFilters]);

  const handleFilterSelect = (groupKey: string, option: string) => {
    premiumSessionFilterStore.toggleFilterOption(groupKey, option);
    onFilterSelect?.(groupKey, option);
  };

  return (
    <FilterSideBar
      expandedGroups={premiumSessionFilterStore.expandedFilterGroups}
      onGroupExpand={premiumSessionFilterStore.toggleFilterGroups}
      onFilterSelect={handleFilterSelect}
      aria-label={t('Filter the list of live sessions')}
    >
      <div className="live-session-filter__header">
        <Text
          className="live-session-filter__title"
          component={TextVariants.h3}
        >
          {title || t('Live sessions')}
        </Text>
      </div>
      {showCatalogFilters && (
        <FilterSideBar.NavGroup title={t('Filters')}>
          <FilterSideBar.ActiveFilterLabels
            groups={filterGroups}
            numLabels={5}
          />
          {filterGroups.map((fg) => (
            <FilterSideBar.FilterGroup key={fg.groupKey} {...fg} />
          ))}
        </FilterSideBar.NavGroup>
      )}
      <RangeFilters />
      {showFullSeatsFilter && (
        <div className="live-session-filter__full-seats-filter pf-v5-theme-dark">
          <Switch
            data-analytics-id="hide-full-sess-chkbox-schedule-sess-lp"
            name="full-session-filter"
            label={t('Hide full sessions')}
            isChecked={premiumSessionFilterStore.hideFullSessions}
            onChange={(event, checked) => {
              premiumSessionFilterStore.hideFullSessions = checked;
              premiumSessionFilterStore.filterEntries();
            }}
            ouiaId="BasicSwitch"
          />
        </div>
      )}
      {showPremiumSessionsWithScheduleDisabled && (
        <div
          className="live-session-filter__video"
          data-analytics-id="prem-vid-free-tier-live-sessions-lp"
        >
          <KalturaWidget
            className="expert-extras-player"
            partnerId={KALTURA_CONFIGS.partnerId}
            playerId={KALTURA_CONFIGS.premiumInFreeTrial.uiConfId}
            entryId={KALTURA_CONFIGS.premiumInFreeTrial.entryId}
            isPlaylist={false}
          />
        </div>
      )}
    </FilterSideBar>
  );
};

export default withErrorBoundary(
  observer(LiveSessionFilters),
  ComponentError,
  handleError,
);
